import memoize from "memoize";
import { interpolate, linear, easeIn, easeInOut, easeOut, circIn, circInOut, circOut, backIn, backInOut, backOut, anticipate, bounceIn, bounceInOut, bounceOut } from "popmotion";
import pMemoize from "p-memoize";
import { filters, helpers, render, getConfig } from "squirrelly";
import fetch from "cross-fetch";
import { loadYoga } from "yoga-layout";
function crossFade({ canvas, scenes, currentTime }) {
  if (scenes && canvas) {
    const activeScenes = scenes.scenes.filter(({ start, duration }) => {
      return currentTime >= start && currentTime <= start + duration;
    });
    if (activeScenes.length <= 0) {
      return;
    }
    const mainCtx = canvas.getContext("2d");
    if (mainCtx) {
      const transitionDuration = scenes.transitionDuration;
      const lastVideoEnd = Math.max(...scenes.scenes.map(({ start, duration }) => start + duration));
      mainCtx.globalCompositeOperation = "lighter";
      mainCtx.clearRect(0, 0, canvas.width, canvas.height);
      activeScenes.forEach(({ start, duration, draw, transitionCanvas }) => {
        mainCtx.globalAlpha = 1;
        transitionCanvas.width = transitionCanvas.height = 0;
        transitionCanvas.width = canvas.width;
        transitionCanvas.height = canvas.height;
        const tCtx = transitionCanvas.getContext("2d");
        if (tCtx) {
          const end = start + duration;
          const isFirst = start === 0;
          const isLast = end === lastVideoEnd;
          draw({
            currentTime,
            canvas: transitionCanvas
          });
          if (!isFirst && currentTime >= start && currentTime <= start + transitionDuration) {
            mainCtx.globalAlpha = Math.max(0, Math.min(1, (currentTime - start) / transitionDuration));
          } else if (!isLast && currentTime >= end - transitionDuration && currentTime <= end) {
            mainCtx.globalAlpha = Math.max(0, Math.min(1, (end - currentTime) / transitionDuration));
          }
          mainCtx.drawImage(transitionCanvas, 0, 0, canvas.width, canvas.height);
        }
      });
    }
  }
}
function normaliseResolution(resolution) {
  if (typeof resolution === "string") {
    const [w, h] = resolution.split(":");
    return Math.min(parseFloat(w), parseFloat(h));
  }
  return resolution;
}
function normaliseAspectRatio(aspectRatio) {
  if (typeof aspectRatio === "string") {
    const [w, h] = aspectRatio.split(":");
    const aW = parseFloat(w);
    const aH = parseFloat(h);
    return aW / aH;
  }
  return aspectRatio;
}
function getSizeDetails({ resolution, aspectRatio }) {
  const numericResolution = normaliseResolution(resolution);
  const numericAspectRatio = normaliseAspectRatio(aspectRatio);
  const widthLong = numericAspectRatio >= 1;
  const shortSide = Math.min(
    widthLong ? numericResolution * numericAspectRatio : numericResolution / numericAspectRatio,
    numericResolution
  );
  const longSide = Math.max(
    widthLong ? numericResolution * numericAspectRatio : numericResolution / numericAspectRatio,
    numericResolution
  );
  return {
    resolution: numericResolution,
    aspectRatio: numericAspectRatio,
    height: widthLong ? shortSide : longSide,
    width: widthLong ? longSide : shortSide
  };
}
function getVideoPosition({ format, resolution, aspectRatio, sourceWidth, sourceHeight }) {
  const { width: drawAreaWidth, height: drawAreaHeight } = getSizeDetails({ resolution, aspectRatio });
  const videoAspectRatio = sourceWidth / sourceHeight;
  const canvasRatioBigger = aspectRatio > videoAspectRatio;
  let scale = 1;
  if (format === "letterbox" || format === "letterbox-video") {
    scale = canvasRatioBigger ? drawAreaHeight / sourceHeight : drawAreaWidth / sourceWidth;
  } else if (format === "squarebox") {
    const shortSide = Math.min(drawAreaHeight, drawAreaWidth);
    scale = sourceWidth >= sourceHeight ? shortSide / sourceHeight : shortSide / sourceWidth;
  } else if (format === "crop") {
    scale = canvasRatioBigger ? drawAreaWidth / sourceWidth : drawAreaHeight / sourceHeight;
  }
  const sWidth = drawAreaWidth / scale;
  const sHeight = drawAreaHeight / scale;
  const sx = (sourceWidth - sWidth) / 2;
  const sy = (sourceHeight - sHeight) / 2;
  return {
    sx,
    sy,
    sWidth,
    sHeight,
    scale
  };
}
function getSafeRect({ source, sx, sy, sw, sh, dx, dy, dw, dh }) {
  const width = (source == null ? void 0 : source.naturalWidth) ?? (source == null ? void 0 : source.videoWidth) ?? (source == null ? void 0 : source.width);
  const height = (source == null ? void 0 : source.naturalHeight) ?? (source == null ? void 0 : source.videoHeight) ?? (source == null ? void 0 : source.height);
  sx = sx ?? 0;
  sy = sy ?? 0;
  sw = sw ?? width;
  sh = sh ?? height;
  if (sw < 0) {
    sx += sw;
    sw = Math.abs(sw);
  }
  if (sh < 0) {
    sy += sh;
    sh = Math.abs(sh);
  }
  if (dw < 0) {
    dx += dw;
    dw = Math.abs(dw);
  }
  if (dh < 0) {
    dy += dh;
    dh = Math.abs(dh);
  }
  const x1 = Math.max(sx, 0);
  const x2 = Math.min(sx + sw, width);
  const y1 = Math.max(sy, 0);
  const y2 = Math.min(sy + sh, height);
  const wRatio = dw / sw;
  const hRatio = dh / sh;
  return [
    source,
    x1,
    y1,
    x2 - x1,
    y2 - y1,
    sx < 0 ? dx - sx * wRatio : dx,
    sy < 0 ? dy - sy * hRatio : dy,
    (x2 - x1) * wRatio,
    (y2 - y1) * hRatio
  ];
}
function drawVideoFormat({
  context,
  source,
  crop,
  format = "letterbox",
  posX,
  posY,
  drawAreaWidth,
  drawAreaHeight,
  sourceWidth,
  sourceHeight,
  cropCanvas,
  letterboxCanvas
}) {
  const { x, y, width, height } = crop ?? {};
  const offsetX = sourceWidth * (x ?? 0);
  const offsetY = sourceHeight * (y ?? 0);
  const cropWidth = sourceWidth * Math.min(1 - (x ?? 0), width ?? 1);
  const cropHeight = sourceHeight * Math.min(1 - (y ?? 0), height ?? 1);
  const cropContext = cropCanvas.getContext("2d");
  if (cropContext) {
    cropCanvas.width = cropWidth;
    cropCanvas.height = cropHeight;
    const safeRect = getSafeRect({
      source,
      sx: offsetX,
      sy: offsetY,
      sw: cropWidth,
      sh: cropHeight,
      dx: 0,
      dy: 0,
      dw: cropWidth,
      dh: cropHeight
    });
    cropContext.drawImage(...safeRect);
  }
  const videoAspectRatio = cropWidth / cropHeight;
  const { sx, sy, sWidth, sHeight, scale } = getVideoPosition({
    resolution: Math.min(drawAreaWidth, drawAreaHeight),
    aspectRatio: drawAreaWidth / drawAreaHeight,
    format,
    sourceWidth: cropWidth,
    sourceHeight: cropHeight
  });
  const canLetterboxVideo = cropCanvas && typeof context.filter !== "undefined";
  if (format === "letterbox" || format === "squarebox" || format === "letterbox-video" && !canLetterboxVideo) {
    if (sx < 0) {
      const letterboxWidth = Math.abs(sx) * scale;
      context.fillStyle = "black";
      context.beginPath();
      context.rect(posX, posY, letterboxWidth, drawAreaHeight);
      context.rect(posX + drawAreaWidth - letterboxWidth, posY, letterboxWidth, drawAreaHeight);
      context.fill();
      context.closePath();
    }
    if (sy < 0) {
      const letterboxHeight = Math.abs(sy) * scale;
      context.fillStyle = "black";
      context.beginPath();
      context.rect(posX, posY, drawAreaWidth, letterboxHeight);
      context.rect(posX, posY + drawAreaHeight - letterboxHeight, drawAreaWidth, letterboxHeight);
      context.fill();
      context.closePath();
    }
  }
  if (format === "letterbox-video" && canLetterboxVideo) {
    const letterboxContext = letterboxCanvas.getContext("2d");
    if (letterboxContext) {
      letterboxCanvas.width = cropWidth;
      letterboxCanvas.height = cropHeight;
      context.save();
      context.globalCompositeOperation = "destination-out";
      context.fillRect(posX, posY, drawAreaWidth, drawAreaHeight);
      const safeRect = getSafeRect({
        source: cropCanvas,
        sx,
        sy,
        sw: sWidth,
        sh: sHeight,
        dx: posX,
        dy: posY,
        dw: drawAreaWidth,
        dh: drawAreaHeight
      });
      context.globalCompositeOperation = "source-over";
      context.drawImage(...safeRect);
      letterboxContext.save();
      const scale2 = 1.2;
      const translateX = -(cropWidth * scale2 - cropWidth) / 2;
      const translateY = -(cropHeight * scale2 - cropHeight) / 2;
      const blurFactor = Math.min(cropWidth / 20, cropHeight / 20);
      letterboxContext.filter = `blur(${blurFactor}px) brightness(20%)`;
      letterboxContext.translate(translateX, translateY);
      letterboxContext.scale(scale2, scale2);
      letterboxContext.drawImage(cropCanvas, 0, 0, cropWidth, cropHeight);
      letterboxContext.restore();
      context.globalCompositeOperation = "destination-over";
      if (sx < 0) {
        const h = drawAreaWidth / videoAspectRatio;
        const ratio = drawAreaHeight / h;
        const safeRect2 = getSafeRect({
          source: letterboxCanvas,
          sy: (1 - ratio) * cropHeight / 2,
          sh: ratio * cropHeight,
          dx: posX,
          dy: posY,
          dw: drawAreaWidth,
          dh: drawAreaHeight
        });
        context.drawImage(...safeRect2);
      }
      if (sy < 0) {
        const w = drawAreaHeight * videoAspectRatio;
        const ratio = drawAreaWidth / w;
        const safeRect2 = getSafeRect({
          source: letterboxCanvas,
          sx: (1 - ratio) * cropWidth / 2,
          sw: ratio * cropWidth,
          dx: posX,
          dy: posY,
          dw: drawAreaWidth,
          dh: drawAreaHeight
        });
        context.drawImage(...safeRect2);
      }
      context.restore();
    }
  } else {
    const safeRect = getSafeRect({
      source: cropCanvas,
      sx,
      sy,
      sw: sWidth,
      sh: sHeight,
      dx: posX,
      dy: posY,
      dw: drawAreaWidth,
      dh: drawAreaHeight
    });
    context.drawImage(...safeRect);
  }
  const dx = sx * -1 * scale;
  const dy = sy * -1 * scale;
  const dw = drawAreaWidth - dx * 2;
  const dh = drawAreaHeight - dy * 2;
  return {
    dx,
    dy,
    dw,
    dh
  };
}
function breakWord({ context, word, maxW = Infinity }) {
  const lines = [];
  const fullWordMetrics = context.measureText(word);
  if (fullWordMetrics.width < maxW) {
    return [{ text: word, metrics: fullWordMetrics }];
  }
  for (let i = 0; i < word.length; i++) {
    const lastLine = lines[lines.length - 1];
    const newText = ((lastLine == null ? void 0 : lastLine.text) ?? "") + word[i];
    const newLineMetrics = context.measureText(newText);
    if (newLineMetrics.width < maxW) {
      lines[lastLine ? lines.length - 1 : 0] = {
        text: newText,
        metrics: newLineMetrics
      };
    } else {
      lines.push({ text: word[i], metrics: context.measureText(word[i]) });
    }
  }
  return lines;
}
const getFontSize = memoize(
  ({ font, scale, context, maxW, maxH, text, minSize, maxSize }) => {
    var _a, _b;
    let fontSize = 0;
    if (maxSize) {
      const multiplier = 1;
      fontSize = (minSize ?? 0) * scale;
      let widestLine = 0;
      while (maxSize * scale > fontSize + multiplier) {
        fontSize += multiplier;
        const lines = getLines({ context, font, fontSize, text, maxW });
        widestLine = lines.reduce((acc, line) => {
          if (line.metrics.width > acc) {
            acc = line.metrics.width;
          }
          return acc;
        }, 0);
        if (widestLine > maxW || lines.length * fontSize > maxH) {
          fontSize -= multiplier;
          break;
        }
      }
    } else {
      fontSize = parseFloat(((_b = (_a = font.match(fontSizeRegex)) == null ? void 0 : _a.groups) == null ? void 0 : _b.value) ?? "0") * scale;
    }
    return fontSize;
  },
  {
    cacheKey: ([{ font, text, minSize, maxSize, scale, maxW, maxH }]) => `${font}_${text}_${minSize}_${maxSize}_${scale}_${maxW}_${maxH}`
  }
);
function parseInset(inset, scale) {
  if (typeof inset === "number") {
    return {
      top: inset * scale,
      right: inset * scale,
      bottom: inset * scale,
      left: inset * scale
    };
  }
  return {
    top: typeof inset.top === "number" ? inset.top * scale : inset.top,
    right: typeof inset.right === "number" ? inset.right * scale : inset.right,
    bottom: typeof inset.bottom === "number" ? inset.bottom * scale : inset.bottom,
    left: typeof inset.left === "number" ? inset.left * scale : inset.left
  };
}
function parseSpacing(spacing, scale) {
  if (typeof spacing === "number") {
    return {
      top: spacing * scale,
      right: spacing * scale,
      bottom: spacing * scale,
      left: spacing * scale
    };
  }
  return {
    top: (typeof spacing.top === "number" ? spacing.top * scale : spacing.top) ?? 0,
    right: (typeof spacing.right === "number" ? spacing.right * scale : spacing.right) ?? 0,
    bottom: (typeof spacing.bottom === "number" ? spacing.bottom * scale : spacing.bottom) ?? 0,
    left: (typeof spacing.left === "number" ? spacing.left * scale : spacing.left) ?? 0
  };
}
function setNodeProperties(yoga, node, { layout, scale = 1 }) {
  const {
    position = "static",
    inset,
    width,
    height,
    minWidth,
    minHeight,
    maxWidth,
    maxHeight,
    flex,
    gap,
    margin,
    padding,
    // Not technically CSS defaults but I prefer column as default
    direction = "column",
    align = "start",
    alignSelf,
    justify = "start"
  } = layout;
  const positions = {
    static: yoga.POSITION_TYPE_STATIC,
    relative: yoga.POSITION_TYPE_RELATIVE,
    absolute: yoga.POSITION_TYPE_ABSOLUTE
  };
  node.setPositionType(positions[position]);
  if (inset) {
    const { top, right, bottom, left } = parseInset(inset, scale);
    if (top) {
      node.setPosition(yoga.EDGE_TOP, top);
    }
    if (right) {
      node.setPosition(yoga.EDGE_RIGHT, right);
    }
    if (bottom) {
      node.setPosition(yoga.EDGE_BOTTOM, bottom);
    }
    if (left) {
      node.setPosition(yoga.EDGE_LEFT, left);
    }
  }
  const directions = {
    row: yoga.FLEX_DIRECTION_ROW,
    column: yoga.FLEX_DIRECTION_COLUMN,
    "row-reverse": yoga.FLEX_DIRECTION_ROW_REVERSE,
    "column-reverse": yoga.FLEX_DIRECTION_COLUMN_REVERSE
  };
  node.setFlexDirection(directions[direction]);
  const aligns = {
    start: yoga.ALIGN_FLEX_START,
    end: yoga.ALIGN_FLEX_END,
    center: yoga.ALIGN_CENTER,
    stretch: yoga.ALIGN_STRETCH
  };
  node.setAlignItems(aligns[align]);
  if (alignSelf) {
    node.setAlignSelf(aligns[alignSelf]);
  }
  const justifies = {
    start: yoga.JUSTIFY_FLEX_START,
    end: yoga.JUSTIFY_FLEX_END,
    center: yoga.JUSTIFY_CENTER,
    "space-between": yoga.JUSTIFY_SPACE_BETWEEN,
    "space-around": yoga.JUSTIFY_SPACE_AROUND,
    "space-evenly": yoga.JUSTIFY_SPACE_EVENLY
  };
  node.setJustifyContent(justifies[justify]);
  if (margin) {
    const { top, right, bottom, left } = parseSpacing(margin, scale);
    node.setMargin(yoga.EDGE_TOP, top);
    node.setMargin(yoga.EDGE_RIGHT, right);
    node.setMargin(yoga.EDGE_BOTTOM, bottom);
    node.setMargin(yoga.EDGE_LEFT, left);
  }
  if (padding) {
    const { top, right, bottom, left } = parseSpacing(padding, scale);
    node.setPadding(yoga.EDGE_TOP, top);
    node.setPadding(yoga.EDGE_RIGHT, right);
    node.setPadding(yoga.EDGE_BOTTOM, bottom);
    node.setPadding(yoga.EDGE_LEFT, left);
  }
  if (flex) {
    node.setFlex(flex);
  }
  node.setGap(yoga.GUTTER_ALL, (gap ?? 0) * scale);
  if (width) {
    node.setWidth(typeof width === "number" ? width * scale : width);
  }
  if (height) {
    if (typeof height === "function") {
      node.setHeight("100%");
    } else {
      node.setHeight(typeof height === "number" ? height * scale : height);
    }
  }
  if (minWidth) {
    node.setMinWidth(typeof minWidth === "number" ? minWidth * scale : minWidth);
  }
  if (minHeight) {
    node.setMinHeight(typeof minHeight === "number" ? minHeight * scale : minHeight);
  }
  if (maxWidth) {
    node.setMaxWidth(typeof maxWidth === "number" ? maxWidth * scale : maxWidth);
  }
  if (maxHeight) {
    node.setMaxHeight(typeof maxHeight === "number" ? maxHeight * scale : maxHeight);
  }
}
function _layoutElements({ canvas, debug = false, scale = 1, position = [] }) {
  position.forEach(({ x, y, width, height, childNodes, element }) => {
    const context = canvas.getContext("2d", { alpha: false });
    if (context) {
      const { draw } = element;
      if (draw) {
        context.save();
        draw({ scale, dx: x, dy: y, dw: width, dh: height });
        if (debug) {
          context.strokeStyle = "red";
          context.strokeRect(x, y, width, height);
        }
      }
      _layoutElements({
        canvas,
        debug,
        scale,
        position: childNodes
      });
      context.restore();
    }
  });
}
function buildNodeTree(yoga, parent, elements, scale) {
  const nodes = elements.map((element, index) => {
    const node = yoga.Node.create();
    const { radius, elements: children = [], draw, ...layout } = element;
    setNodeProperties(yoga, node, { layout, scale });
    parent.insertChild(node, index);
    return {
      node,
      element,
      childNodes: buildNodeTree(yoga, node, children, scale)
    };
  });
  parent.calculateLayout();
  return nodes;
}
function calculatePosition(root, tree, parentX = 0, parentY = 0) {
  const { top: rootY, left: rootX } = root.getComputedLayout();
  return tree.map(({ node, element, childNodes }) => {
    const { top, left, width, height } = node.getComputedLayout();
    const x = parentX + left;
    const y = parentY + top;
    return {
      x: rootX + x,
      y: rootY + y,
      width,
      height,
      element,
      childNodes: calculatePosition(root, childNodes, x, y)
    };
  });
}
function recalculateDimensions(tree, scale) {
  tree.forEach(({ node, element, childNodes }) => {
    const { height } = element;
    if (height && typeof height === "function") {
      const width = node.getComputedWidth();
      const maxHeight = node.getComputedHeight();
      const h = height({ width, maxHeight, scale });
      node.setHeight(h);
    }
    recalculateDimensions(childNodes, scale);
  });
}
function layoutElements({ yoga, canvas, origin, elements, ...args }) {
  const o = { inset: { top: 0, left: 0 }, width: canvas.width, height: canvas.height, ...origin };
  const root = yoga.Node.create();
  setNodeProperties(yoga, root, { layout: o });
  root.calculateLayout();
  const { width, height } = root.getComputedLayout();
  const scale = Math.min(width, height) / 1080;
  const tree = buildNodeTree(yoga, root, elements, scale);
  recalculateDimensions(tree, scale);
  root.calculateLayout();
  const position = calculatePosition(root, tree);
  return _layoutElements({ ...args, scale, canvas, position });
}
const easingMap = {
  linear,
  easeIn,
  easeInOut,
  easeOut,
  circIn,
  circInOut,
  circOut,
  backIn,
  backInOut,
  backOut,
  anticipate,
  bounceIn,
  bounceInOut,
  bounceOut
};
const createInterpolate = memoize(
  (from, to, { clamp = true, ease = "easeIn", ...options } = {}) => {
    return interpolate(from, to, { ...options, clamp, ease: easingMap[ease] });
  },
  {
    cacheKey: ([from, to, options]) => JSON.stringify({ from, to, options })
  }
);
const supportedFonts = [
  {
    family: "Noto Arabic",
    regex: /\p{Script=Arabic}/u,
    url: "https://media-player-fonts.s3.ap-southeast-2.amazonaws.com/NotoSansArabic-VariableFont_wdth%2Cwght.woff2",
    style: "normal"
  },
  {
    family: "Noto Chinese",
    regex: /\p{Script=Han}/u,
    url: "https://media-player-fonts.s3.ap-southeast-2.amazonaws.com/NotoSansSC-VariableFont_wght.woff2",
    style: "normal"
  },
  {
    family: "Noto Hebrew",
    regex: /\p{Script=Hebrew}/u,
    url: "https://media-player-fonts.s3.ap-southeast-2.amazonaws.com/NotoSansHebrew-VariableFont_wdth%2Cwght.woff2",
    style: "normal"
  },
  {
    family: "Noto Hindi",
    regex: /\p{Script=Devanagari}/u,
    url: "https://media-player-fonts.s3.ap-southeast-2.amazonaws.com/NotoSansDevanagari-VariableFont_wdth%2Cwght.woff2",
    style: "normal"
  },
  {
    family: "Noto Japanese",
    regex: /\p{Script=Han}|\p{Script=Katakana}|\p{Script=Hiragana}/u,
    url: "https://media-player-fonts.s3.ap-southeast-2.amazonaws.com/NotoSansJP-VariableFont_wght.woff2",
    style: "normal"
  },
  {
    family: "Noto Korean",
    regex: /\p{Script=Hangul}/u,
    url: "https://media-player-fonts.s3.ap-southeast-2.amazonaws.com/NotoSansKR-VariableFont_wght.woff2",
    style: "normal"
  },
  {
    family: "Noto Color Emoji",
    regex: /\p{Extended_Pictographic}/u,
    url: "https://media-player-fonts.s3.ap-southeast-2.amazonaws.com/NotoColorEmoji-Regular.woff2",
    style: "normal"
  }
];
function appendFontFamilies({ text, font }) {
  let newFont = font;
  supportedFonts.forEach(({ family, regex }) => {
    if (regex.exec(text)) {
      newFont += `, ${family}`;
    }
  });
  return newFont;
}
const PILL_FRACTION = 1.8;
const fontSizeRegex = /(?<value>\d+\.?\d*)(?=px)/;
function parseRadius(radius, scale = 1) {
  if (typeof radius === "number") {
    return {
      topLeft: radius * scale,
      topRight: radius * scale,
      bottomRight: radius * scale,
      bottomLeft: radius * scale
    };
  }
  return {
    topLeft: ((radius == null ? void 0 : radius.topLeft) ?? 0) * scale,
    topRight: ((radius == null ? void 0 : radius.topRight) ?? 0) * scale,
    bottomRight: ((radius == null ? void 0 : radius.bottomRight) ?? 0) * scale,
    bottomLeft: ((radius == null ? void 0 : radius.bottomLeft) ?? 0) * scale
  };
}
function visibleRecursive(element) {
  const { visible, node } = element;
  const childElements = [...(node == null ? void 0 : node.elements) ?? [], ...(element == null ? void 0 : element.elements) ?? []];
  return (
    // If we're explicitly visible or invisible return this value
    visible ?? // Otherwise only show the node if the node has a value that's not undefined
    (node && node.type !== "box" && node.type !== "gradient" ? !!node.value : childElements == null ? void 0 : childElements.some((element2) => visibleRecursive(element2)))
  );
}
function mapElements({
  yoga,
  debug = false,
  canvas,
  cropCanvas,
  posterCanvas,
  letterboxCanvas,
  elements = [],
  currentTime,
  contentTime,
  sceneTime,
  totalDuration,
  contentDuration,
  sceneDuration
}) {
  return elements.map((element) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const { node, transient = [{ from: 0, duration: Infinity }] } = element;
    const mainCtx = canvas.getContext("2d");
    const timers = {
      total: currentTime,
      content: contentTime,
      scene: sceneTime
    };
    const durations = {
      total: totalDuration,
      content: contentDuration,
      scene: sceneDuration
    };
    const activeTransients = transient.reduce((acc, { type = "scene", from, duration, animation }) => {
      const relativeFrom = from < 0 ? durations[type] + from : from;
      if (timers[type] >= relativeFrom && timers[type] <= relativeFrom + duration) {
        acc.push({
          type,
          animation,
          duration,
          from: relativeFrom
        });
      }
      return acc;
    }, []);
    if (mainCtx && activeTransients.length > 0 && visibleRecursive(element)) {
      let width = element.width;
      let height = element.height;
      if ((node == null ? void 0 : node.type) === "text" && node.value) {
        const { align = "left" } = node;
        const font = appendFontFamilies({ text: node.value, font: node.font });
        mainCtx.save();
        mainCtx.font = font;
        mainCtx.textBaseline = "top";
        mainCtx.textAlign = align;
        if (!node.wrap) {
          width = mainCtx.measureText(node.value).width;
          height = parseFloat(((_b = (_a = font.match(fontSizeRegex)) == null ? void 0 : _a.groups) == null ? void 0 : _b.value) ?? "0");
        } else {
          height = ({ width: width2, maxHeight, scale }) => {
            var _a2, _b2;
            const fontSize = getFontSize({
              font,
              scale,
              context: mainCtx,
              maxW: width2,
              maxH: maxHeight,
              text: node.value ?? "",
              minSize: (_a2 = node.fill) == null ? void 0 : _a2.minSize,
              maxSize: (_b2 = node.fill) == null ? void 0 : _b2.maxSize
            });
            const lines = getLines({ context: mainCtx, font, fontSize, text: node.value ?? "", maxW: width2 });
            return lines.length * fontSize;
          };
        }
        mainCtx.restore();
      }
      if ((node == null ? void 0 : node.type) === "image" && node.image && node.size) {
        const logoWidth = ((_c = node.image) == null ? void 0 : _c.naturalWidth) ?? ((_d = node.image) == null ? void 0 : _d.width);
        const logoHeight = ((_e = node.image) == null ? void 0 : _e.naturalHeight) ?? ((_f = node.image) == null ? void 0 : _f.height);
        const logoAspectRatio = logoWidth / logoHeight;
        const desiredWidth = (_g = node.size) == null ? void 0 : _g.width;
        const desiredHeight = (_h = node.size) == null ? void 0 : _h.height;
        width = desiredWidth ?? (logoAspectRatio >= 1 ? (desiredHeight ?? 0) * logoAspectRatio : (desiredHeight ?? 0) / logoAspectRatio);
        height = desiredHeight ?? (logoAspectRatio < 1 ? (desiredWidth ?? 0) * logoAspectRatio : (desiredWidth ?? 0) / logoAspectRatio);
      }
      return {
        ...element,
        width,
        height,
        elements: mapElements({
          yoga,
          debug,
          canvas,
          cropCanvas,
          posterCanvas,
          letterboxCanvas,
          elements: element.elements,
          currentTime,
          contentTime,
          sceneTime,
          totalDuration,
          contentDuration,
          sceneDuration
        }),
        draw: ({ dx, dy, dw, dh, scale }) => {
          var _a2, _b2, _c2, _d2, _e2, _f2, _g2, _h2, _i, _j, _k, _l, _m;
          activeTransients.forEach((transient2) => {
            const {
              type: transientType = "scene",
              from,
              duration,
              animation = { type: "fade", duration: 1 / 3, easing: { in: "easeOut", out: "easeOut" } }
            } = transient2;
            if (animation) {
              const {
                type: animationType,
                duration: animationDuration,
                easing: { in: animationEasingIn = "easeOut", out: animationEasingOut = "easeOut" } = {}
              } = animation;
              if (from > 0 && timers[transientType] <= from + animationDuration) {
                switch (animationType) {
                  case "fade": {
                    const fadeIn = createInterpolate([from, from + animationDuration], [0, 1], {
                      ease: animationEasingIn
                    });
                    mainCtx.globalAlpha = fadeIn(timers[transientType]);
                    break;
                  }
                  case "slide": {
                    const { options } = animation;
                    const slideMap = {
                      up: [
                        { x: 0, y: dh },
                        { x: 0, y: 0 }
                      ],
                      right: [
                        { x: dw, y: 0 },
                        { x: 0, y: 0 }
                      ],
                      down: [
                        { x: 0, y: -dh },
                        { x: 0, y: 0 }
                      ],
                      left: [
                        { x: -dw, y: 0 },
                        { x: 0, y: 0 }
                      ]
                    };
                    const slideIn = createInterpolate(
                      [from, from + animationDuration],
                      slideMap[options.direction.in],
                      { ease: animationEasingIn }
                    );
                    const { x, y } = slideIn(timers[transientType]);
                    mainCtx.translate(x, y);
                    break;
                  }
                }
              }
              if (
                // Don't fade out if we hit the end of the video as the fade ends
                from + duration < durations[transientType] && from + duration - animationDuration <= timers[transientType]
              ) {
                switch (animationType) {
                  case "fade": {
                    const fadeOut = createInterpolate(
                      [from + duration - animationDuration, from + duration],
                      [1, 0],
                      {
                        ease: animationEasingOut
                      }
                    );
                    mainCtx.globalAlpha = fadeOut(timers[transientType]);
                    break;
                  }
                  case "slide": {
                    const { options } = animation;
                    const slideMap = {
                      up: [
                        { x: 0, y: 0 },
                        { x: 0, y: -dh }
                      ],
                      right: [
                        { x: 0, y: 0 },
                        { x: dw, y: 0 }
                      ],
                      down: [
                        { x: 0, y: 0 },
                        { x: 0, y: dh }
                      ],
                      left: [
                        { x: 0, y: 0 },
                        { x: -dw, y: 0 }
                      ]
                    };
                    const slideOut = createInterpolate(
                      [from + duration - animationDuration, from + duration],
                      slideMap[options.direction.out],
                      { ease: animationEasingOut }
                    );
                    const { x, y } = slideOut(timers[transientType]);
                    mainCtx.translate(x, y);
                    break;
                  }
                }
              }
            }
          });
          if (node) {
            const { type } = node;
            if (type === "box") {
              const { shadow: { color = "rgba(0, 0, 0, 0.5)", blur = 0, offset: { x = 0, y = 0 } = {} } = {} } = node;
              mainCtx.save();
              mainCtx.fillStyle = color;
              mainCtx.shadowColor = color;
              mainCtx.shadowBlur = blur;
              mainCtx.shadowOffsetX = x + dw + canvas.width;
              mainCtx.shadowOffsetY = y + dh + canvas.height;
              mainCtx.beginPath();
              if (!element.radius) {
                mainCtx.rect(dx - dw - canvas.width, dy - dh - canvas.height, dw, dh);
              } else {
                const { topLeft, topRight, bottomRight, bottomLeft } = parseRadius(element.radius, scale);
                mainCtx.roundRect(dx - dw - canvas.width, dy - dh - canvas.height, dw, dh, [
                  topLeft,
                  topRight,
                  bottomRight,
                  bottomLeft
                ]);
              }
              mainCtx.fill();
              mainCtx.closePath();
              mainCtx.restore();
            }
            if (element.radius) {
              const { topLeft, topRight, bottomRight, bottomLeft } = parseRadius(element.radius, scale);
              mainCtx.beginPath();
              mainCtx.moveTo(dx + Math.min(dh / PILL_FRACTION, topLeft), dy);
              mainCtx.lineTo(dx + dw - Math.min(dh / PILL_FRACTION, topRight), dy);
              mainCtx.quadraticCurveTo(dx + dw, dy, dx + dw, dy + Math.min(dh / PILL_FRACTION, topRight));
              mainCtx.lineTo(dx + dw, dy + dh - Math.min(dh / PILL_FRACTION, bottomRight));
              mainCtx.quadraticCurveTo(
                dx + dw,
                dy + dh,
                dx + dw - Math.min(dh / PILL_FRACTION, bottomRight),
                dy + dh
              );
              mainCtx.lineTo(dx + Math.min(dh / PILL_FRACTION, bottomLeft), dy + dh);
              mainCtx.quadraticCurveTo(dx, dy + dh, dx, dy + dh - Math.min(dh / PILL_FRACTION, bottomLeft));
              mainCtx.lineTo(dx, dy + Math.min(dh / PILL_FRACTION, topLeft));
              mainCtx.quadraticCurveTo(dx, dy, dx + Math.min(dh / PILL_FRACTION, topLeft), dy);
              mainCtx.closePath();
              if (debug) {
                mainCtx.strokeStyle = "blue";
                mainCtx.stroke();
              }
              mainCtx.clip();
            }
            if (type === "box") {
              mainCtx.save();
              mainCtx.fillStyle = node.background;
              mainCtx.fillRect(dx, dy, dw, dh);
              mainCtx.restore();
            }
            if (type === "text" && node.value) {
              const { align = "left" } = node;
              const font = appendFontFamilies({ text: node.value, font: node.font });
              const fontSize = getFontSize({
                font,
                scale,
                context: mainCtx,
                maxW: dw,
                maxH: dh,
                text: node.value ?? "",
                minSize: (_a2 = node.fill) == null ? void 0 : _a2.minSize,
                maxSize: (_b2 = node.fill) == null ? void 0 : _b2.maxSize
              });
              mainCtx.font = font.replace(fontSizeRegex, fontSize.toString());
              mainCtx.textBaseline = "top";
              mainCtx.fillStyle = node.color;
              mainCtx.textAlign = align;
              const textXPosMap = {
                left: dx,
                center: dx + dw / 2,
                right: dx + dw
              };
              if (node.wrap) {
                const lines = getLines({ context: mainCtx, font, fontSize, text: node.value, maxW: dw });
                lines.forEach((line, index) => {
                  mainCtx.fillText(line.text, textXPosMap[align], dy + fontSize * index);
                });
              } else {
                mainCtx.fillText(node.value, textXPosMap[align], dy);
              }
            }
            if (type === "image" && node.image) {
              mainCtx.drawImage(node.image, dx, dy, dw, dh);
            }
            if (type === "video" && (((_c2 = node.video) == null ? void 0 : _c2.node) || ((_d2 = node.video) == null ? void 0 : _d2.getSource))) {
              const format = node.format ?? "letterbox";
              const video = ((_f2 = (_e2 = node.video) == null ? void 0 : _e2.getSource) == null ? void 0 : _f2.call(_e2, sceneTime)) ?? node.video.node;
              const sourceWidth = (video == null ? void 0 : video.videoWidth) ?? (video == null ? void 0 : video.naturalWidth) ?? (video == null ? void 0 : video.width);
              const sourceHeight = (video == null ? void 0 : video.videoHeight) ?? (video == null ? void 0 : video.naturalHeight) ?? (video == null ? void 0 : video.height);
              if (sourceWidth && sourceHeight) {
                const {
                  dx: videoX,
                  dy: videoY,
                  dw: videoW,
                  dh: videoH
                } = drawVideoFormat({
                  context: mainCtx,
                  cropCanvas,
                  letterboxCanvas,
                  source: video,
                  format,
                  crop: (_g2 = node == null ? void 0 : node.value) == null ? void 0 : _g2.crop,
                  sourceWidth,
                  sourceHeight,
                  posX: dx,
                  posY: dy,
                  drawAreaWidth: dw,
                  drawAreaHeight: dh
                });
                if (node.elements) {
                  drawElements({
                    yoga,
                    debug,
                    canvas,
                    cropCanvas,
                    posterCanvas,
                    letterboxCanvas,
                    elements: node.elements,
                    currentTime,
                    contentTime,
                    sceneTime,
                    totalDuration,
                    contentDuration,
                    sceneDuration,
                    origin: {
                      inset: {
                        top: Math.max(dy, dy + videoY),
                        left: Math.max(dx, dx + videoX)
                      },
                      width: Math.min(dw, videoW),
                      height: Math.min(dh, videoH)
                    }
                  });
                }
              }
              const posterDuration = 1 / 3;
              if (node.poster && currentTime >= 0 && currentTime <= posterDuration) {
                mainCtx.save();
                mainCtx.globalAlpha = 1 - currentTime / posterDuration;
                const posterWidth = ((_h2 = node.poster) == null ? void 0 : _h2.naturalWidth) ?? ((_i = node.poster) == null ? void 0 : _i.width);
                const posterHeight = ((_j = node.poster) == null ? void 0 : _j.naturalHeight) ?? ((_k = node.poster) == null ? void 0 : _k.height);
                posterCanvas.width = posterCanvas.height = 0;
                posterCanvas.width = dw;
                posterCanvas.height = dh;
                const posterCtx = posterCanvas.getContext("2d");
                if (posterCtx && posterWidth && posterHeight) {
                  const {
                    dx: videoX,
                    dy: videoY,
                    dw: videoW,
                    dh: videoH
                  } = drawVideoFormat({
                    context: posterCtx,
                    cropCanvas,
                    letterboxCanvas,
                    source: node.poster,
                    format,
                    crop: (_l = node == null ? void 0 : node.value) == null ? void 0 : _l.crop,
                    sourceWidth: posterWidth,
                    sourceHeight: posterHeight,
                    posX: 0,
                    posY: 0,
                    drawAreaWidth: dw,
                    drawAreaHeight: dh
                  });
                  if (node.elements) {
                    drawElements({
                      yoga,
                      debug,
                      canvas: posterCanvas,
                      cropCanvas,
                      posterCanvas,
                      letterboxCanvas,
                      elements: node.elements,
                      currentTime,
                      contentTime,
                      sceneTime,
                      totalDuration,
                      contentDuration,
                      sceneDuration,
                      origin: {
                        inset: {
                          top: Math.max(0, videoY),
                          left: Math.max(0, videoX)
                        },
                        width: Math.min(dw, videoW),
                        height: Math.min(dh, videoH)
                      }
                    });
                  }
                  mainCtx.drawImage(posterCanvas, dx, dy, dw, dh);
                  mainCtx.restore();
                }
              }
            }
            if (type === "gradient" && node.value) {
              const gradient = mainCtx.createLinearGradient(dx + dw, dy, dx + dw, dy + dh);
              (_m = node.value) == null ? void 0 : _m.forEach((stop) => {
                gradient.addColorStop(stop.percent, stop.color);
              });
              mainCtx.fillStyle = gradient;
              mainCtx.fillRect(dx, dy, dw, dh);
            }
          }
        }
      };
    }
  }).filter(Boolean);
}
function drawElements({
  yoga,
  debug,
  canvas,
  cropCanvas,
  posterCanvas,
  letterboxCanvas,
  elements,
  currentTime,
  contentTime,
  sceneTime,
  totalDuration,
  contentDuration,
  sceneDuration,
  ...rest
}) {
  return layoutElements({
    ...rest,
    yoga,
    debug,
    canvas,
    elements: mapElements({
      yoga,
      debug,
      canvas,
      cropCanvas,
      posterCanvas,
      letterboxCanvas,
      elements,
      currentTime,
      contentTime,
      sceneTime,
      totalDuration,
      contentDuration,
      sceneDuration
    })
  });
}
const getLines = memoize(
  ({ context, font, fontSize, text, maxW = Infinity }) => {
    try {
      context.save();
      context.font = font.replace(fontSizeRegex, fontSize.toString());
      const newLines = text.split("\n");
      return newLines.flatMap((line) => {
        const words = line.split(" ").filter(Boolean);
        if (!words.length) {
          return { text: "", metrics: context.measureText("") };
        }
        return words.slice(1).reduce((acc, word) => {
          const prevLine = acc[acc.length - 1].text;
          const newLine = `${prevLine} ${word}`.trim();
          const newMetrics = context.measureText(newLine);
          if (newMetrics.width > maxW) {
            acc = [...acc, ...breakWord({ context, word, maxW })];
          } else {
            acc[acc.length - 1] = {
              text: newLine,
              metrics: newMetrics
            };
          }
          return acc;
        }, breakWord({ context, word: words[0], maxW }));
      });
    } finally {
      context.restore();
    }
  },
  {
    cacheKey: ([{ font, fontSize, text, maxW }]) => `${font}_${fontSize}_${text}_${maxW}`
  }
);
const VTT_LINE_REGEX = /\d{0,2}:?\d{2}:\d{2}\.\d{3}\s-->\s\d{0,2}:?\d{2}:\d{2}\.\d{3}\s{1,2}.*/g;
const VTT_TIMESTAMP_REGEX = /\d{0,2}:?\d{2}:\d{2}\.\d{3}/g;
function vttToLines(vtt) {
  if (!vtt) {
    return [];
  }
  const match = vtt.match(VTT_LINE_REGEX);
  if (!match) {
    return [];
  }
  return match == null ? void 0 : match.map((line) => {
    const [timestamps, text] = line.replace("\r\n", "\n").split("\n");
    const [start, end] = timestamps.match(VTT_TIMESTAMP_REGEX) || [];
    return { start, end, text };
  });
}
function parseTimestamp(timestamp) {
  const [hours, minutes, seconds] = timestamp.split(":");
  if (!seconds) {
    return parseInt(hours) * 60 + parseInt(minutes);
  }
  return parseInt(hours) * 60 * 60 + parseInt(minutes) * 60 + parseFloat(seconds);
}
function drawCaptions({ canvas, dx, dy, dw, dh, sceneTime, vttCaptions }) {
  if (vttCaptions) {
    const captions = vttToLines(vttCaptions);
    const activeCaption = captions.find(({ start, end }) => {
      return start && end && sceneTime >= parseTimestamp(start) && sceneTime < parseTimestamp(end);
    });
    if (activeCaption) {
      const context = canvas.getContext("2d");
      if (context) {
        const resolution = Math.min(dw, dh);
        const fontSize = resolution * 0.04;
        const font = appendFontFamilies({
          text: vttCaptions,
          font: `400 ${fontSize}px Roobert Captions`
        });
        context.font = font;
        context.textBaseline = "middle";
        const maxWidth = dw * 0.9;
        const lines = getLines({ context, font, fontSize, text: activeCaption.text, maxW: maxWidth });
        lines.forEach(({ text, metrics }, index) => {
          const textPosX = dx + (dw - metrics.width) / 2;
          const textPosY = dy + (dh * 0.85 - (fontSize + resolution * 0.03) * (lines.length - 1 - index));
          const boxPosOffset = fontSize * 0.8;
          const boxPadding = fontSize / 1.5;
          const boxPosX = dx + (dw - metrics.width - boxPadding) / 2;
          const boxPosY = textPosY - boxPosOffset;
          const boxWidth = metrics.width + boxPadding;
          const boxHeight = boxPosOffset * 2;
          const currentAlpha = context.globalAlpha;
          context.save();
          context.globalAlpha = 0.8 * currentAlpha;
          context.fillStyle = "#000000";
          context.beginPath();
          context.roundRect(boxPosX, boxPosY, boxWidth, boxHeight, resolution * 0.01);
          context.fill();
          context.closePath();
          context.globalAlpha = currentAlpha;
          context.fillStyle = "#FFFFFF";
          context.fillText(text, textPosX, textPosY);
          context.restore();
        });
      }
    }
  }
}
const template = {
  id: "046f363c-960a-449c-ae5e-984ac6ab58c2",
  version: "2",
  name: "None",
  entityId: "none",
  templateId: "a95e537e-fb4d-4d2f-9c72-86c0925c7176",
  layouts: [
    {
      name: "none",
      tree: `[
        {
          "width": "100%",
          "height": "100%",
          "node": {
            "type": "video",
            "format": {{ it.videoFormat }},
            "value": {
              "id": {{ it.id }},
              "key": {{ it.key }},
              "src": {{@async resolve-source({ sources: [it.mp4, it.input] }) /}},
              "playlist": {{@async resolve-source({ sources: [it.playlist] }) /}},
              "poster": {{ it.poster }},
              "captions": {{ it.captions }},
              "duration": {{ it.duration }},
              "startOffset": {{ it.startOffset }},
              "endOffset": {{ it.endOffset }},
              "crop": {
                "x": {{ it.crop?.x }},
                "y": {{ it.crop?.y }},
                "width": {{ it.crop?.width }},
                "height": {{ it.crop?.height }}
              }
            }
          }
        }
      ]`
    }
  ],
  content: [
    {
      scenes: [
        {
          layout: "none",
          data: `{
            "id": {{ it.root?.answer?.id }},
            "key": {{ it.root?.answer?.id + '-intro' }},
            "mp4": {{ it.root?.answer?.media?.video }},
            "input": {{ it.root?.answer?.media?.input }},
            "playlist": {{ it.root?.answer?.media?.playlist }},
            "poster": {{ it.root?.answer?.media?.thumbnail }},
            "captions": {{ it.root?.answer?.captions?.current }},
            "duration": {{ it.root?.answer?.metadata?.duration }},
            "startOffset": {{ it.root?.answer?.settings?.startOffset }},
            "endOffset": {{ it.root?.answer?.settings?.endOffset }},
            "crop": {
              "x": {{ it.root?.answer?.settings?.crop?.x }},
              "y": {{ it.root?.answer?.settings?.crop?.y }},
              "width": {{ it.root?.answer?.settings?.crop?.width }},
              "height": {{ it.root?.answer?.settings?.crop?.height }}
            }
          }`
        }
      ],
      root: "{{ it.intro?.questions?.items }}"
    },
    {
      scenes: [
        {
          layout: "none",
          data: `{
            "id": {{ it.root?.answer?.id }},
            "key": {{ it.root?.answer?.id + '-' + it.index }},
            "mp4": {{ it.root?.answer?.media?.video }},
            "input": {{ it.root?.answer?.media?.input }},
            "playlist": {{ it.root?.answer?.media?.playlist }},
            "poster": {{ it.root?.answer?.media?.thumbnail }},
            "captions": {{ it.root?.answer?.captions?.current }},
            "duration": {{ it.root?.answer?.metadata?.duration }},
            "startOffset": {{ it.root?.answer?.settings?.startOffset }},
            "endOffset": {{ it.root?.answer?.settings?.endOffset }},
            "crop": {
              "x": {{ it.root?.answer?.settings?.crop?.x }},
              "y": {{ it.root?.answer?.settings?.crop?.y }},
              "width": {{ it.root?.answer?.settings?.crop?.width }},
              "height": {{ it.root?.answer?.settings?.crop?.height }}
            }
          }`
        }
      ],
      root: "{{ it.questions?.items }}"
    },
    {
      scenes: [
        {
          layout: "none",
          data: `{
            "id": {{ it.root?.answer?.id }},
            "key": {{ it.root?.answer?.id + '-outro' }},
            "mp4": {{ it.root?.answer?.media?.video }},
            "input": {{ it.root?.answer?.media?.input }},
            "playlist": {{ it.root?.answer?.media?.playlist }},
            "poster": {{ it.root?.answer?.media?.thumbnail }},
            "captions": {{ it.root?.answer?.captions?.current }},
            "duration": {{ it.root?.answer?.metadata?.duration }},
            "startOffset": {{ it.root?.answer?.settings?.startOffset }},
            "endOffset": {{ it.root?.answer?.settings?.endOffset }},
            "crop": {
              "x": {{ it.root?.answer?.settings?.crop?.x }},
              "y": {{ it.root?.answer?.settings?.crop?.y }},
              "width": {{ it.root?.answer?.settings?.crop?.width }},
              "height": {{ it.root?.answer?.settings?.crop?.height }}
            }
          }`
        }
      ],
      root: "{{ it.outro?.questions?.items }}"
    }
  ],
  system: true,
  createdAt: (/* @__PURE__ */ new Date()).toISOString(),
  updatedAt: (/* @__PURE__ */ new Date()).toISOString()
};
const none = {
  id: "073977ec-6384-4329-b7c5-8275fb513e74",
  name: "None",
  entityId: "none",
  templateId: "a95e537e-fb4d-4d2f-9c72-86c0925c7176",
  templates: [template],
  meta: {
    frameRate: 30,
    resolution: 720,
    transitionDuration: 1 / 3
  },
  system: true,
  default: 0,
  createdAt: (/* @__PURE__ */ new Date()).toISOString(),
  updatedAt: (/* @__PURE__ */ new Date()).toISOString()
};
async function imageFactory() {
  if (typeof window === "undefined") {
    const skiaCanvas = await import("skia-canvas");
    return new skiaCanvas.Image();
  }
  return new Image();
}
const getImage = pMemoize(async function getImage2(src) {
  if (!src) {
    return null;
  }
  return new Promise((resolve, reject) => {
    imageFactory().then((image) => {
      image.onload = () => {
        resolve(image);
      };
      image.onerror = (e) => {
        reject(e);
      };
      image.src = src;
    });
  });
});
async function resolveElement({ element, meta }, resolveVideo, resolveVideoMeta) {
  var _a, _b, _c;
  let newElement = { ...element };
  const { node } = element;
  if ((node == null ? void 0 : node.type) === "video" && (((_a = node == null ? void 0 : node.value) == null ? void 0 : _a.src) || ((_b = node == null ? void 0 : node.value) == null ? void 0 : _b.playlist))) {
    const video = await resolveVideo(node.value, meta);
    let poster;
    try {
      poster = await getImage(node.value.poster);
    } catch {
      poster = null;
    }
    newElement = {
      ...newElement,
      node: {
        ...node,
        video,
        poster,
        meta: await resolveVideoMeta(node.value, video.node, meta)
      }
    };
  }
  if ((node == null ? void 0 : node.type) === "image" && node.value) {
    let image;
    try {
      image = await getImage(node.value);
    } catch {
      image = null;
    }
    newElement = {
      ...newElement,
      node: {
        ...node,
        image
      }
    };
  }
  if (newElement.node && ((_c = element.node) == null ? void 0 : _c.elements)) {
    newElement.node.elements = await Promise.all(
      element.node.elements.map((element2) => resolveElement({ element: element2, meta }, resolveVideo, resolveVideoMeta))
    );
  }
  if (element.elements) {
    newElement.elements = await Promise.all(
      element.elements.map((element2) => resolveElement({ element: element2, meta }, resolveVideo, resolveVideoMeta))
    );
  }
  return newElement;
}
async function canvasFactory() {
  if (typeof window === "undefined") {
    const skiaCanvas = await import("skia-canvas");
    const canvas = new skiaCanvas.Canvas();
    canvas.gpu = false;
    return canvas;
  }
  return document.createElement("canvas");
}
async function checkSource(src) {
  var _a;
  try {
    if ((_a = await fetch(src, { method: "HEAD" })) == null ? void 0 : _a.ok) {
      return src;
    }
  } catch {
    return void 0;
  }
  return void 0;
}
async function getSources({ sources }) {
  let src = null;
  if (sources) {
    const sPromises = sources.filter(Boolean).map((src2) => checkSource(src2));
    src = (await Promise.all(sPromises)).find(Boolean);
  }
  return src;
}
function getVideoNode(elements = []) {
  var _a;
  for (const element of elements) {
    if (((_a = element == null ? void 0 : element.node) == null ? void 0 : _a.type) === "video") {
      return element.node;
    }
    const node = getVideoNode(element == null ? void 0 : element.elements);
    if (node) {
      return node;
    }
  }
}
function calculateContentsDuration(contents, transitionDuration) {
  return contents.reduce((acc, scenes, i) => {
    return [
      ...acc,
      scenes.map(({ duration }, j) => {
        const isLastScene = i === contents.length - 1 && j === scenes.length - 1;
        if (isLastScene) {
          return duration;
        }
        return duration - transitionDuration;
      })
    ];
  }, []);
}
function isPlainObject(value) {
  if (value === null || typeof value !== "object") {
    return false;
  }
  if (Object.getPrototypeOf(value) === null) {
    return true;
  }
  let proto = value;
  while (Object.getPrototypeOf(proto) !== null) {
    proto = Object.getPrototypeOf(proto);
  }
  return Object.getPrototypeOf(value) === proto;
}
function sanitizeJSON(unsanitized) {
  return unsanitized.replace(/\\/g, "\\\\").replace(/\//g, "\\/").replace(/\n/g, "\\n").replace(/\r/g, "\\r").replace(/\t/g, "\\t").replace(/\f/g, "\\f").replace(/"/g, '\\"');
}
function sum(arr) {
  return arr.flatMap((duration) => duration).reduce((a, b) => a + b, 0);
}
const loadYogaLayout = pMemoize(() => {
  return loadYoga();
});
filters.define("parse", (data) => {
  if (typeof data === "undefined") {
    return null;
  }
  if (typeof data === "string") {
    return `"${sanitizeJSON(data)}"`;
  }
  if (isPlainObject(data) || Array.isArray(data)) {
    return JSON.stringify(data);
  }
  return data;
});
helpers.define(
  "resolve-source",
  pMemoize(
    async (content) => {
      const { sources } = content.params[0] ?? {};
      const validSources = sources == null ? void 0 : sources.filter(Boolean);
      const blob = validSources == null ? void 0 : validSources.find((source2) => source2.includes("blob:"));
      const local = validSources == null ? void 0 : validSources.find((source2) => !source2.includes("http"));
      const source = blob ?? local ?? await getSources({ sources });
      return source ? `"${source}"` : "null";
    },
    {
      cacheKey: ([content]) => JSON.stringify(content.params[0] ?? {})
    }
  )
);
const overrideRender = (template2, data, config) => {
  return render(
    template2,
    data,
    getConfig({
      autoEscape: false,
      defaultFilter: "parse",
      async: true,
      asyncHelpers: ["resolve-source"],
      ...config
    })
  );
};
async function templateToScenes({ data, template: argTemplate, burnCaptions = false, debug = false }, resolveVideo, resolveVideoMeta) {
  var _a, _b, _c, _d;
  const yoga = await loadYogaLayout();
  const templateInstance = argTemplate ?? none;
  if (!((_a = templateInstance.templates) == null ? void 0 : _a.length)) {
    throw new Error(`No templates found for template instance: ${templateInstance.id}`);
  }
  let validTemplate = templateInstance.templates.find(({ version }) => version === TEMPLATE_VERSION);
  if (!validTemplate) {
    const invalidVersions = templateInstance.templates.map(({ version }) => `"${version}"`).join(", ");
    console.warn(`Template renderer expects template version "${TEMPLATE_VERSION}" but got ${invalidVersions}`);
    validTemplate = templateInstance.templates[templateInstance.templates.length - 1];
  }
  const contents = await Promise.all(
    validTemplate.content.map(async ({ scenes: scenes2, root: contentRoot }) => {
      const renderData = {
        ...templateInstance.options,
        ...data,
        intro: templateInstance.intro,
        outro: templateInstance.outro
      };
      async function contentToSceneConfig(rootData, index) {
        return Promise.all(
          scenes2.map(async ({ layout, duration: templateDuration, data: templateData, options: templateOptions }) => {
            var _a2, _b2, _c2;
            const sceneData = { ...renderData, index, root: rootData };
            const expandedData = templateData ? JSON.parse(await overrideRender(templateData, sceneData)) : {};
            const expandedOptions = templateOptions ? JSON.parse(await overrideRender(templateOptions, sceneData)) : {};
            const expandedDuration = templateDuration ? parseFloat(await overrideRender(templateDuration, sceneData)) : NaN;
            const layoutTree = (_a2 = validTemplate.layouts.find((l) => l.name === layout)) == null ? void 0 : _a2.tree;
            if (!layoutTree) {
              throw new Error(`Layout tree "${layout}" not found in template "${validTemplate.id}" layouts.`);
            }
            const elements = JSON.parse(
              await overrideRender(layoutTree, {
                ...expandedData,
                ...expandedOptions
              })
            );
            const resolvedElements = await Promise.all(
              elements.flatMap(
                (element) => resolveElement({ element, meta: templateInstance.meta }, resolveVideo, resolveVideoMeta)
              )
            );
            const videoNode = getVideoNode(resolvedElements);
            const duration = (isNaN(expandedDuration) ? null : expandedDuration) ?? ((_b2 = videoNode == null ? void 0 : videoNode.meta) == null ? void 0 : _b2.duration) ?? 0;
            return {
              duration,
              captions: (_c2 = videoNode == null ? void 0 : videoNode.meta) == null ? void 0 : _c2.captions,
              elements: resolvedElements
            };
          })
        );
      }
      if (contentRoot) {
        const expandedRoot = JSON.parse(await overrideRender(contentRoot, renderData));
        if (Array.isArray(expandedRoot)) {
          return (await Promise.all(expandedRoot.map(async (val, index) => contentToSceneConfig(val, index)))).flatMap((x) => x);
        }
        return contentToSceneConfig(expandedRoot, null);
      }
      return contentToSceneConfig(data, null);
    })
  );
  const noVideo = contents.every((sceneConfig) => {
    return sceneConfig.every((scene) => {
      const node = getVideoNode(scene.elements);
      return !(node == null ? void 0 : node.value.src) && !(node == null ? void 0 : node.value.playlist);
    });
  });
  if (noVideo) {
    const failedVideoIds = contents.flatMap(
      (sceneConfig) => sceneConfig.map((scene) => {
        const node = getVideoNode(scene.elements);
        return !(node == null ? void 0 : node.value.src) && !(node == null ? void 0 : node.value.playlist) && (node == null ? void 0 : node.value.id);
      }).filter(Boolean)
    ).join(", ");
    throw new Error(`Video(s) with ID ${failedVideoIds} failed to load, video can not be processed`);
  }
  const noDurations = contents.every((sceneConfig) => {
    return sceneConfig.every((scene) => scene.duration <= 0);
  });
  if (noDurations) {
    throw new Error("No scenes have any duration, video can not be processed");
  }
  const filteredContents = contents.reduce((acc, sceneConfig) => {
    const filteredSceneConfig = sceneConfig.filter((scene) => scene.duration > 0);
    if (filteredSceneConfig.length > 0) {
      acc = [...acc, filteredSceneConfig];
    }
    return acc;
  }, []);
  const frameRate = ((_b = templateInstance.meta) == null ? void 0 : _b.frameRate) ?? 30;
  const resolution = ((_c = templateInstance.meta) == null ? void 0 : _c.resolution) ?? 720;
  const transitionDuration = ((_d = templateInstance.meta) == null ? void 0 : _d.transitionDuration) ?? 1 / 3;
  const durations = calculateContentsDuration(filteredContents, transitionDuration);
  const totalDuration = sum(durations);
  const scenes = (await Promise.all(
    filteredContents.map((scenes2, i) => {
      const contentDuration = sum(durations[i]);
      const prevContentDurations = sum(durations.slice(0, i));
      return Promise.all(
        scenes2.map(async ({ duration, captions, elements }, j) => {
          var _a2, _b2;
          const cropCanvas = await canvasFactory();
          const letterboxCanvas = await canvasFactory();
          const transitionCanvas = await canvasFactory();
          const posterCanvas = await canvasFactory();
          const videoNode = getVideoNode(elements);
          const prevDurations = sum([...durations.slice(0, i), ...durations[i].slice(0, j)]);
          const draw = ({ canvas, currentTime }) => {
            const contentTime = Math.max(0, currentTime - prevContentDurations);
            const sceneTime = Math.max(0, currentTime - prevDurations);
            drawElements({
              yoga,
              debug,
              canvas,
              cropCanvas,
              posterCanvas,
              letterboxCanvas,
              elements,
              currentTime,
              contentTime,
              sceneTime,
              totalDuration,
              contentDuration,
              sceneDuration: duration
            });
            if (captions && burnCaptions) {
              drawCaptions({
                canvas,
                dx: 0,
                dy: 0,
                dw: canvas.width,
                dh: canvas.height,
                vttCaptions: captions,
                sceneTime: sceneTime + ((videoNode == null ? void 0 : videoNode.meta.start) ?? 0)
              });
            }
          };
          return {
            draw,
            duration,
            transitionCanvas,
            start: prevDurations,
            video: videoNode == null ? void 0 : videoNode.video,
            videoMeta: videoNode == null ? void 0 : videoNode.meta,
            audio: {
              duration,
              src: (_a2 = videoNode == null ? void 0 : videoNode.value) == null ? void 0 : _a2.src,
              start: (_b2 = videoNode == null ? void 0 : videoNode.meta) == null ? void 0 : _b2.start
            }
          };
        })
      );
    })
  )).flatMap((scenes2) => scenes2);
  return {
    scenes,
    frameRate,
    resolution,
    transitionDuration,
    fonts: validTemplate.fonts ?? [],
    duration: totalDuration
  };
}
const TEMPLATE_VERSION = "2";
export {
  TEMPLATE_VERSION,
  appendFontFamilies,
  crossFade,
  drawCaptions,
  drawElements,
  drawVideoFormat,
  getSizeDetails,
  normaliseAspectRatio,
  normaliseResolution,
  parseTimestamp,
  supportedFonts,
  templateToScenes,
  vttToLines
};
//# sourceMappingURL=canvas-video.js.map
